import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Shield, Music, Shirt, Car, Users, DollarSign, Package, 
  Plus, Trash2, X, Save, Wand2, Loader2, Sparkles, Download,
  Tag, BarChart3, TrendingUp, TrendingDown, Calendar, Percent, Hash, AlertTriangle,
  Mail, Send, Mic2, Volume2, FileText, MessageCircle, Link2, Upload, ShoppingCart, FlaskConical, Layers, Trophy, Star, Gift, Zap, CheckCircle, XCircle, Crown, Bell, RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { useAuth } from "../App";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from "recharts";
import {
  OverviewTab, AnalyticsTab, PromoCodesTab, AIDesignTab,
  BeatsTab, MerchTab, CarsTab, UsersTab, LowStockTab,
  EmailCampaignsTab, VoiceTagTab, BlogAdminTab, LiveChatAdminTab,
  AffiliatesTab, AbandonedCartsTab, ABTestingTab, RewardsTab,
  BundlesTab, FlashSalesTab, GiftCardsTab, CarSubmissionsTab,
  ProducersAdminTab, TicketsAdminTab, SoundKitsAdminTab,
  AdvancedAnalyticsTab, SubscriptionsAdminTab, ContestsAdminTab
} from "../components/admin/AdminTabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [beats, setBeats] = useState([]);
  const [merch, setMerch] = useState([]);
  const [users, setUsers] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Form states
  const [beatForm, setBeatForm] = useState({
    title: "", bpm: 140, key: "Am", genre: "Trap", tags: "",
    audio_url: "", preview_url: "", image_url: "",
    basic_price: 29.99, premium_price: 79.99, unlimited_price: 149.99, exclusive_price: 499.99
  });
  const [merchForm, setMerchForm] = useState({
    name: "", description: "", category: "hoodies", price: 49.99, images: "", variants: ""
  });
  const [carForm, setCarForm] = useState({
    title: "", description: "", media_type: "image", media_url: "", thumbnail_url: "", tags: ""
  });
  const [promoForm, setPromoForm] = useState({
    code: "", discount_type: "percentage", discount_value: 10,
    min_order_amount: 0, max_uses: 0, description: "", expires_at: ""
  });
  
  // AI Design Generator state
  const [aiForm, setAiForm] = useState({
    prompt: "", style: "streetwear", product_type: "hoodie"
  });
  const [generatingDesign, setGeneratingDesign] = useState(false);
  const [generatedDesigns, setGeneratedDesigns] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [carSubmissions, setCarSubmissions] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [subscribers, setSubscribers] = useState({ subscribers: [], total: 0 });
  const [voiceTagExists, setVoiceTagExists] = useState(false);
  const [generatingTag, setGeneratingTag] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatReply, setChatReply] = useState("");
  const [affiliates, setAffiliates] = useState([]);
  const [abandonedCarts, setAbandonedCarts] = useState({ carts: [], total: 0 });
  const [autoRecoverLoading, setAutoRecoverLoading] = useState(false);
  const [recoveryLogs, setRecoveryLogs] = useState([]);
  const [abTests, setAbTests] = useState([]);
  const [stemsEditBeatId, setStemsEditBeatId] = useState(null);
  const [stemsData, setStemsData] = useState([]);
  const [loyaltyOverview, setLoyaltyOverview] = useState(null);
  const [adminBundles, setAdminBundles] = useState([]);
  const [adminFlashSales, setAdminFlashSales] = useState([]);
  const [adminGiftCards, setAdminGiftCards] = useState({ cards: [], total_sold: 0, total_redeemed: 0 });
  const [producers, setProducers] = useState([]);
  const [producerBeats, setProducerBeats] = useState([]);
  const [adminTickets, setAdminTickets] = useState([]);
  const [adminSoundkits, setAdminSoundkits] = useState([]);
  const [advancedAnalytics, setAdvancedAnalytics] = useState(null);
  const [affiliatePayouts, setAffiliatePayouts] = useState([]);
  const [adminSubscriptions, setAdminSubscriptions] = useState(null);
  const [adminContests, setAdminContests] = useState([]);
  const [showContestDialog, setShowContestDialog] = useState(false);
  const [contestForm, setContestForm] = useState({ title: "", description: "", prize: "", genre: "any" });
  const [pushForm, setPushForm] = useState({ title: "", message: "", target: "all", category: "general" });
  const [pushHistory, setPushHistory] = useState([]);
  const [sendingPush, setSendingPush] = useState(false);
  const [scImportLoading, setScImportLoading] = useState(false);
  const [printifyStatus, setPrintifyStatus] = useState(null);
  const [printifySetupLoading, setPrintifySetupLoading] = useState(false);
  const [printifySetupResult, setPrintifySetupResult] = useState(null);
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  const [showBeatDialog, setShowBeatDialog] = useState(false);
  const [showMerchDialog, setShowMerchDialog] = useState(false);
  const [showCarDialog, setShowCarDialog] = useState(false);
  const [showPromoDialog, setShowPromoDialog] = useState(false);
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [showBlogDialog, setShowBlogDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [campaignForm, setCampaignForm] = useState({ subject: "", body: "", recipient_type: "all" });
  const [blogForm, setBlogForm] = useState({ title: "", content: "", excerpt: "", image_url: "", tags: "", published: true });
  const [bulkBeats, setBulkBeats] = useState([{ title: "", bpm: 140, key: "Am", genre: "Trap", audio_url: "", image_url: "", basic_price: 29.99, premium_price: 79.99, unlimited_price: 149.99, exclusive_price: 499.99 }]);
  const [showBundleDialog, setShowBundleDialog] = useState(false);
  const [showFlashSaleDialog, setShowFlashSaleDialog] = useState(false);
  const [bundleForm, setBundleForm] = useState({ name: "", description: "", beat_ids: [], price: 0, image_url: "" });
  const [flashSaleForm, setFlashSaleForm] = useState({ name: "", discount_percent: 20, applies_to: "all", product_ids: [], start_time: "", end_time: "" });

  useEffect(() => {
    if (authLoading) return;
    if (!user?.is_admin) {
      navigate("/");
      return;
    }
    fetchData();
  }, [user, navigate, authLoading]);

  // Fetch analytics when tab changes
  useEffect(() => {
    if (activeTab === "analytics" && !analytics) fetchAnalytics();
    if (activeTab === "promos" && promoCodes.length === 0) fetchPromoCodes();
    if (activeTab === "ai-design" && generatedDesigns.length === 0) fetchGeneratedDesigns();
    if (activeTab === "low-stock" && lowStockItems.length === 0) fetchLowStock();
    if (activeTab === "email" && campaigns.length === 0) { fetchCampaigns(); fetchSubscribers(); }
    if (activeTab === "voice-tag") checkVoiceTag();
    if (activeTab === "blog-admin" && blogPosts.length === 0) fetchBlogPosts();
    if (activeTab === "live-chat" && chatSessions.length === 0) fetchChatSessions();
    if (activeTab === "affiliates" && affiliates.length === 0) fetchAffiliates();
    if (activeTab === "affiliates") fetchAffiliatePayouts();
    if (activeTab === "abandoned-carts") fetchAbandonedCarts();
    if (activeTab === "ab-testing") fetchAbTests();
    if (activeTab === "rewards") fetchLoyaltyOverview();
    if (activeTab === "bundles") fetchBundles();
    if (activeTab === "flash-sales") fetchFlashSales();
    if (activeTab === "gift-cards") fetchGiftCards();
    if (activeTab === "submissions") fetchCarSubmissions();
    if (activeTab === "producers") { fetchProducers(); fetchProducerBeats(); }
    if (activeTab === "tickets") fetchAdminTickets();
    if (activeTab === "soundkits-admin") fetchAdminSoundkits();
    if (activeTab === "advanced-analytics") fetchAdvancedAnalytics();
    if (activeTab === "subs-admin") fetchAdminSubscriptions();
    if (activeTab === "contests-admin") fetchAdminContests();
    if (activeTab === "push-notifs") fetchPushHistory();
    if (activeTab === "printify-setup") fetchPrintifyStatus();
  }, [activeTab]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [statsRes, beatsRes, merchRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers, withCredentials: true }),
        axios.get(`${API}/beats`, { headers, withCredentials: true }),
        axios.get(`${API}/merch`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/users`, { headers, withCredentials: true })
      ]);
      setStats(statsRes.data);
      setBeats(beatsRes.data);
      setMerch(merchRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const fetchPromoCodes = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/promo-codes`, { headers, withCredentials: true });
      setPromoCodes(res.data);
    } catch (error) {
      toast.error("Failed to load promo codes");
    }
  };

  const fetchAnalytics = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/analytics?days=30`, { headers, withCredentials: true });
      setAnalytics(res.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    }
  };

  const fetchGeneratedDesigns = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/ai/designs`, { headers, withCredentials: true });
      setGeneratedDesigns(response.data);
    } catch (error) {
      console.error("Failed to fetch designs:", error);
    }
  };

  const fetchLowStock = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/low-stock?threshold=10`, { headers, withCredentials: true });
      setLowStockItems(res.data.items);
    } catch (error) {
      console.error("Failed to fetch low stock");
    }
  };

  const fetchCampaigns = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/campaigns`, { headers, withCredentials: true });
      setCampaigns(res.data);
    } catch (e) { console.error("Failed to fetch campaigns"); }
  };

  const fetchSubscribers = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/subscribers`, { headers, withCredentials: true });
      setSubscribers(res.data);
    } catch (e) { console.error("Failed to fetch subscribers"); }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/admin/campaigns`, campaignForm, { headers, withCredentials: true });
      toast.success(res.data.note || "Campaign sent!");
      setShowCampaignDialog(false);
      setCampaignForm({ subject: "", body: "", recipient_type: "all" });
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to create campaign");
    }
  };

  const checkVoiceTag = async () => {
    try {
      const res = await fetch(`${API}/static/voice_tag.mp3`, { method: "HEAD" });
      setVoiceTagExists(res.ok);
    } catch (e) { setVoiceTagExists(false); }
  };

  const handleGenerateVoiceTag = async () => {
    setGeneratingTag(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/generate-voice-tag`, {}, { headers, withCredentials: true });
      toast.success("Voice tag generated!");
      setVoiceTagExists(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate voice tag");
    } finally {
      setGeneratingTag(false);
    }
  };

  // Blog
  const fetchBlogPosts = async () => {
    try {
      const res = await axios.get(`${API}/blog?published_only=false`);
      setBlogPosts(res.data);
    } catch (e) {}
  };
  const handleCreateBlogPost = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/blog`, {
        ...blogForm, tags: blogForm.tags.split(",").map(t => t.trim()).filter(Boolean)
      }, { headers, withCredentials: true });
      toast.success("Blog post created!");
      setShowBlogDialog(false);
      setBlogForm({ title: "", content: "", excerpt: "", image_url: "", tags: "", published: true });
      fetchBlogPosts();
    } catch (e) { toast.error("Failed to create post"); }
  };
  const handleDeleteBlogPost = async (postId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/blog/${postId}`, { headers, withCredentials: true });
      setBlogPosts(prev => prev.filter(p => p.post_id !== postId));
      toast.success("Post deleted");
    } catch (e) { toast.error("Failed to delete post"); }
  };

  // Chat
  const fetchChatSessions = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/chats?status=all`, { headers, withCredentials: true });
      setChatSessions(res.data);
    } catch (e) {}
  };
  const openChat = async (sessionId) => {
    setActiveChatId(sessionId);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/chat/messages/${sessionId}`, { headers, withCredentials: true });
      setChatMessages(res.data);
    } catch (e) {}
  };
  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!chatReply.trim()) return;
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/chat/message`, { message: chatReply, session_id: activeChatId }, { headers, withCredentials: true });
      setChatMessages(prev => [...prev, res.data]);
      setChatReply("");
    } catch (e) { toast.error("Failed to send"); }
  };

  // Affiliates
  const fetchAffiliates = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/affiliates`, { headers, withCredentials: true });
      setAffiliates(res.data);
    } catch (e) {}
  };

  const fetchAbandonedCarts = async () => {
    try {
      const headers = getAuthHeaders();
      const [cartsRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/abandoned-carts?hours=24`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/abandoned-carts/recovery-logs`, { headers, withCredentials: true })
      ]);
      setAbandonedCarts(cartsRes.data);
      setRecoveryLogs(logsRes.data.logs || []);
    } catch (e) {}
  };

  const handleSoundCloudImport = async () => {
    const profileUrl = prompt("Enter SoundCloud profile URL:", "https://soundcloud.com/gaschamberlin");
    if (!profileUrl) return;
    
    setScImportLoading(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/admin/soundcloud/import`, { profile_url: profileUrl }, { headers, withCredentials: true });
      toast.success(`Imported ${res.data.imported} tracks! (${res.data.skipped} already existed)`);
      fetchBeats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "SoundCloud import failed");
    } finally {
      setScImportLoading(false);
    }
  };

  const handleAutoRecover = async () => {
    setAutoRecoverLoading(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/admin/abandoned-carts/auto-recover`, { hours: 24, max_emails: 50 }, { headers, withCredentials: true });
      toast.success(`Auto-recovery complete: ${res.data.emails_sent} emails sent out of ${res.data.carts_found} carts`);
      fetchAbandonedCarts();
    } catch (e) {
      toast.error("Auto-recovery failed");
    } finally {
      setAutoRecoverLoading(false);
    }
  };

  const handleMarkContacted = async (userId) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/admin/abandoned-carts/${userId}/mark-contacted`, {}, { headers, withCredentials: true });
      if (res.data.email_sent) {
        toast.success("Recovery email sent successfully!");
      } else {
        toast.success("Marked as contacted (email could not be sent)");
      }
      fetchAbandonedCarts();
    } catch (e) { toast.error("Failed to send recovery email"); }
  };

  const fetchAbTests = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/ab-tests`, { headers, withCredentials: true });
      setAbTests(res.data);
    } catch (e) {}
  };

  const fetchPushHistory = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/push/history`, { headers, withCredentials: true });
      setPushHistory(res.data.notifications || []);
    } catch (e) {}
  };

  const fetchPrintifyStatus = async () => {
    try {
      const r = await axios.get(`${API}/printify/setup-status`, { headers: getAuthHeaders(), withCredentials: true });
      setPrintifyStatus(r.data);
    } catch (e) {
      setPrintifyStatus({ error: e.message });
    }
    try {
      const r = await axios.get(`${API}/printify/webhook/status`, { headers: getAuthHeaders(), withCredentials: true });
      setWebhookStatus(r.data);
    } catch (e) {
      setWebhookStatus({ error: e.message });
    }
  };

  const handlePrintifySetup = async () => {
    if (!window.confirm("This will create all 51 products on your Printify account using collection artwork. Continue?")) return;
    setPrintifySetupLoading(true);
    setPrintifySetupResult(null);
    try {
      toast.info("Setting up Printify products — this may take 2-3 minutes...", { duration: 10000 });
      const r = await axios.post(`${API}/printify/auto-setup`, {}, {
        headers: getAuthHeaders(), withCredentials: true, timeout: 300000
      });
      setPrintifySetupResult(r.data);
      toast.success(`Done! ${r.data?.summary?.created || 0} products created on Printify.`);
      fetchPrintifyStatus();
    } catch (e) {
      toast.error("Printify setup failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setPrintifySetupLoading(false);
    }
  };

  const handleRegisterWebhooks = async () => {
    setWebhookLoading(true);
    try {
      const r = await axios.post(`${API}/printify/webhook/register`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(`Webhooks registered! ${r.data?.registered?.length || 0} topics active.`);
      fetchPrintifyStatus();
    } catch (e) {
      toast.error("Webhook registration failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleManualSync = async () => {
    setSyncLoading(true);
    try {
      const r = await axios.post(`${API}/printify/sync-tracking`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(`Sync complete — ${r.data?.synced || 0} orders updated.`);
    } catch (e) {
      toast.error("Sync failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSendPush = async (e) => {
    e.preventDefault();
    setSendingPush(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/admin/push/send`, pushForm, { headers, withCredentials: true });
      toast.success(`Push sent to ${res.data.recipients} recipients!`);
      setPushForm({ title: "", message: "", target: "all", category: "general" });
      fetchPushHistory();
    } catch (e) {
      toast.error("Failed to send push notification");
    } finally {
      setSendingPush(false);
    }
  };

  const [showAbTestDialog, setShowAbTestDialog] = useState(false);
  const [abTestForm, setAbTestForm] = useState({
    name: "", merch_id: "", variant_a_label: "Original", variant_b_label: "Test Variant",
    variant_a_value: "", variant_b_value: "", metric: "conversion_rate"
  });

  const handleCreateAbTest = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/ab-tests`, abTestForm, { headers, withCredentials: true });
      toast.success("A/B test created");
      setShowAbTestDialog(false);
      setAbTestForm({ name: "", merch_id: "", variant_a_label: "Original", variant_b_label: "Test Variant", variant_a_value: "", variant_b_value: "", metric: "conversion_rate" });
      fetchAbTests();
    } catch (e) { toast.error("Failed to create test"); }
  };

  const handleToggleAbTest = async (testId, status) => {
    try {
      const headers = getAuthHeaders();
      const newStatus = status === "active" ? "paused" : "active";
      await axios.put(`${API}/admin/ab-tests/${testId}`, { status: newStatus }, { headers, withCredentials: true });
      toast.success(`Test ${newStatus}`);
      fetchAbTests();
    } catch (e) { toast.error("Failed to update"); }
  };

  const handleDeleteAbTest = async (testId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/ab-tests/${testId}`, { headers, withCredentials: true });
      toast.success("Test deleted");
      fetchAbTests();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const [showStemsDialog, setShowStemsDialog] = useState(false);

  const handleEditStems = async (beatId) => {
    setStemsEditBeatId(beatId);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/beats/${beatId}/stems`, { headers, withCredentials: true });
      setStemsData(res.data.stems || []);
      setShowStemsDialog(true);
    } catch (e) {
      setStemsData([]);
      setShowStemsDialog(true);
    }
  };

  const handleSaveStems = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/admin/beats/${stemsEditBeatId}/stems`, { stems: stemsData }, { headers, withCredentials: true });
      toast.success("Stems saved");
      setShowStemsDialog(false);
    } catch (e) { toast.error("Failed to save stems"); }
  };

  // Loyalty/Rewards
  const fetchLoyaltyOverview = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/loyalty/overview`, { headers, withCredentials: true });
      setLoyaltyOverview(res.data);
    } catch (e) {}
  };

  const fetchBundles = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/bundles`, { headers, withCredentials: true });
      setAdminBundles(res.data);
    } catch (e) {}
  };

  const fetchFlashSales = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/flash-sales`, { headers, withCredentials: true });
      setAdminFlashSales(res.data);
    } catch (e) {}
  };

  const fetchGiftCards = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/gift-cards`, { headers, withCredentials: true });
      setAdminGiftCards(res.data);
    } catch (e) {}
  };

  const handleCreateBundle = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/bundles`, bundleForm, { headers, withCredentials: true });
      toast.success("Bundle created!");
      setShowBundleDialog(false);
      setBundleForm({ name: "", description: "", beat_ids: [], price: 0, image_url: "" });
      fetchBundles();
    } catch (e) { toast.error("Failed to create bundle"); }
  };

  const handleDeleteBundle = async (bundleId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/bundles/${bundleId}`, { headers, withCredentials: true });
      toast.success("Bundle deleted");
      fetchBundles();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const handleCreateFlashSale = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/flash-sales`, flashSaleForm, { headers, withCredentials: true });
      toast.success("Flash sale created!");
      setShowFlashSaleDialog(false);
      setFlashSaleForm({ name: "", discount_percent: 20, applies_to: "all", product_ids: [], start_time: "", end_time: "" });
      fetchFlashSales();
    } catch (e) { toast.error("Failed to create flash sale"); }
  };

  const handleDeleteFlashSale = async (saleId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/flash-sales/${saleId}`, { headers, withCredentials: true });
      toast.success("Flash sale deleted");
      fetchFlashSales();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const fetchCarSubmissions = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/car-submissions`, { headers, withCredentials: true });
      setCarSubmissions(res.data);
    } catch (e) {}
  };

  const handleApproveSubmission = async (subId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/car-submissions/${subId}/approve`, {}, { headers, withCredentials: true });
      toast.success("Submission approved and added to Garage!");
      fetchCarSubmissions();
    } catch (e) { toast.error("Failed to approve"); }
  };

  const handleRejectSubmission = async (subId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/car-submissions/${subId}/reject`, {}, { headers, withCredentials: true });
      toast.success("Submission rejected");
      fetchCarSubmissions();
    } catch (e) { toast.error("Failed to reject"); }
  };

  // Producer management
  const fetchProducers = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/producers`, { headers, withCredentials: true });
      setProducers(res.data);
    } catch (e) {}
  };

  const fetchProducerBeats = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/producer-beats`, { headers, withCredentials: true });
      setProducerBeats(res.data);
    } catch (e) {}
  };

  const handleApproveProducer = async (producerId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/producers/${producerId}/approve`, {}, { headers, withCredentials: true });
      toast.success("Producer approved!");
      fetchProducers();
    } catch (e) { toast.error("Failed to approve"); }
  };

  const handleRejectProducer = async (producerId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/producers/${producerId}/reject`, {}, { headers, withCredentials: true });
      toast.success("Producer rejected");
      fetchProducers();
    } catch (e) { toast.error("Failed to reject"); }
  };

  const handleApproveProducerBeat = async (subId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/producer-beats/${subId}/approve`, {}, { headers, withCredentials: true });
      toast.success("Beat approved and added to catalog!");
      fetchProducerBeats();
    } catch (e) { toast.error("Failed to approve beat"); }
  };

  const handleRejectProducerBeat = async (subId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/producer-beats/${subId}/reject`, {}, { headers, withCredentials: true });
      toast.success("Beat rejected");
      fetchProducerBeats();
    } catch (e) { toast.error("Failed to reject beat"); }
  };

  // Batch D handlers
  const fetchAdminTickets = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/tickets`, { headers, withCredentials: true });
      setAdminTickets(res.data);
    } catch (e) {}
  };

  const handleUpdateTicketStatus = async (ticketId, status) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/admin/tickets/${ticketId}/status`, { status }, { headers, withCredentials: true });
      toast.success(`Ticket ${status}`);
      fetchAdminTickets();
    } catch (e) { toast.error("Failed to update"); }
  };

  const handleReplyTicket = async (ticketId, message) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/tickets/${ticketId}/reply`, { message }, { headers, withCredentials: true });
      toast.success("Reply sent");
      fetchAdminTickets();
    } catch (e) { toast.error("Failed to reply"); }
  };

  const fetchAdminSoundkits = async () => {
    try {
      const res = await axios.get(`${API}/soundkits/`);
      setAdminSoundkits(res.data);
    } catch (e) {}
  };

  const [showSoundkitDialog, setShowSoundkitDialog] = useState(false);
  const [soundkitForm, setSoundkitForm] = useState({ name: "", description: "", category: "drum_kit", price: 19.99, image_url: "", tags: "", sample_count: 0, format: "WAV" });

  const handleCreateSoundkit = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      const payload = {
        ...soundkitForm,
        tags: soundkitForm.tags.split(",").map(t => t.trim()).filter(Boolean)
      };
      await axios.post(`${API}/admin/soundkits`, payload, { headers, withCredentials: true });
      toast.success("Sound kit created!");
      setShowSoundkitDialog(false);
      setSoundkitForm({ name: "", description: "", category: "drum_kit", price: 19.99, image_url: "", tags: "", sample_count: 0, format: "WAV" });
      fetchAdminSoundkits();
    } catch (e) { toast.error("Failed to create"); }
  };

  const handleDeleteSoundkit = async (kitId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/soundkits/${kitId}`, { headers, withCredentials: true });
      toast.success("Sound kit deleted");
      fetchAdminSoundkits();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const fetchAdvancedAnalytics = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/analytics/advanced`, { headers, withCredentials: true });
      setAdvancedAnalytics(res.data);
    } catch (e) {}
  };

  const fetchAffiliatePayouts = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/affiliate-payouts`, { headers, withCredentials: true });
      setAffiliatePayouts(res.data);
    } catch (e) {}
  };

  const handleApprovePayout = async (payoutId) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/admin/affiliate-payouts/${payoutId}/approve`, {}, { headers, withCredentials: true });
      toast.success("Payout approved");
      fetchAffiliatePayouts();
    } catch (e) { toast.error("Failed to approve"); }
  };

  const fetchAdminSubscriptions = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/admin/subscriptions`, { headers, withCredentials: true });
      setAdminSubscriptions(res.data);
    } catch (e) {}
  };

  const fetchAdminContests = async () => {
    try {
      const res = await axios.get(`${API}/contests/`);
      setAdminContests(res.data);
    } catch (e) {}
  };

  const handleCreateContest = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/contests`, contestForm, { headers, withCredentials: true });
      toast.success("Contest created!");
      setShowContestDialog(false);
      setContestForm({ title: "", description: "", prize: "", genre: "any" });
      fetchAdminContests();
    } catch (e) { toast.error("Failed to create contest"); }
  };

  const handleUpdateContestStatus = async (contestId, status) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/admin/contests/${contestId}/status`, { status }, { headers, withCredentials: true });
      toast.success(`Contest ${status}`);
      fetchAdminContests();
    } catch (e) { toast.error("Failed to update"); }
  };

  const [showAwardDialog, setShowAwardDialog] = useState(false);
  const [awardForm, setAwardForm] = useState({ user_id: "", points: 100, reason: "Admin bonus" });
  const [showBonusEventDialog, setShowBonusEventDialog] = useState(false);
  const [bonusEventForm, setBonusEventForm] = useState({ name: "", multiplier: 2.0, starts_at: "", ends_at: "", description: "" });

  const handleAwardPoints = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/loyalty/award`, awardForm, { headers, withCredentials: true });
      toast.success(`Awarded ${awardForm.points} points!`);
      setShowAwardDialog(false);
      setAwardForm({ user_id: "", points: 100, reason: "Admin bonus" });
      fetchLoyaltyOverview();
    } catch (e) { toast.error("Failed to award points"); }
  };

  const handleCreateBonusEvent = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/loyalty/bonus-event`, bonusEventForm, { headers, withCredentials: true });
      toast.success("Bonus event created!");
      setShowBonusEventDialog(false);
      setBonusEventForm({ name: "", multiplier: 2.0, starts_at: "", ends_at: "", description: "" });
      fetchLoyaltyOverview();
    } catch (e) { toast.error("Failed to create event"); }
  };

  const handleDeleteBonusEvent = async (eventId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/loyalty/bonus-events/${eventId}`, { headers, withCredentials: true });
      toast.success("Event deactivated");
      fetchLoyaltyOverview();
    } catch (e) { toast.error("Failed to delete"); }
  };

  // Bulk Upload
  const handleBulkUpload = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      const payload = { beats: bulkBeats.map(b => ({ ...b, tags: "" })) };
      const res = await axios.post(`${API}/admin/beats/bulk`, payload, { headers, withCredentials: true });
      toast.success(`${res.data.created} beats uploaded!`);
      setShowBulkDialog(false);
      setBulkBeats([{ title: "", bpm: 140, key: "Am", genre: "Trap", audio_url: "", image_url: "", basic_price: 29.99, premium_price: 79.99, unlimited_price: 149.99, exclusive_price: 499.99 }]);
      fetchData();
    } catch (e) { toast.error("Failed to bulk upload"); }
  };

  const handleCreateBeat = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      const payload = {
        ...beatForm,
        tags: beatForm.tags.split(",").map(t => t.trim()).filter(Boolean)
      };
      await axios.post(`${API}/admin/beats`, payload, { headers, withCredentials: true });
      toast.success("Beat created successfully!");
      setShowBeatDialog(false);
      setBeatForm({
        title: "", bpm: 140, key: "Am", genre: "Trap", tags: "",
        audio_url: "", preview_url: "", image_url: "",
        basic_price: 29.99, premium_price: 79.99, unlimited_price: 149.99, exclusive_price: 499.99
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to create beat");
    }
  };

  const handleCreateMerch = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      let variants = [];
      if (merchForm.variants) {
        try { variants = JSON.parse(merchForm.variants); } catch { variants = []; }
      }
      const payload = {
        ...merchForm,
        images: merchForm.images.split(",").map(i => i.trim()).filter(Boolean),
        variants
      };
      await axios.post(`${API}/admin/merch`, payload, { headers, withCredentials: true });
      toast.success("Merch created successfully!");
      setShowMerchDialog(false);
      setMerchForm({ name: "", description: "", category: "hoodies", price: 49.99, images: "", variants: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create merch");
    }
  };

  const handleCreateCar = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      const payload = {
        ...carForm,
        tags: carForm.tags.split(",").map(t => t.trim()).filter(Boolean)
      };
      await axios.post(`${API}/admin/cars`, payload, { headers, withCredentials: true });
      toast.success("Car media created successfully!");
      setShowCarDialog(false);
      setCarForm({ title: "", description: "", media_type: "image", media_url: "", thumbnail_url: "", tags: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create car media");
    }
  };

  const handleCreatePromo = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeaders();
      const payload = {
        ...promoForm,
        expires_at: promoForm.expires_at || null
      };
      await axios.post(`${API}/admin/promo-codes`, payload, { headers, withCredentials: true });
      toast.success("Promo code created!");
      setShowPromoDialog(false);
      setPromoForm({ code: "", discount_type: "percentage", discount_value: 10, min_order_amount: 0, max_uses: 0, description: "", expires_at: "" });
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create promo code");
    }
  };

  const handleTogglePromo = async (promoId, currentActive) => {
    try {
      const headers = getAuthHeaders();
      if (currentActive) {
        await axios.delete(`${API}/admin/promo-codes/${promoId}`, { headers, withCredentials: true });
      } else {
        await axios.put(`${API}/admin/promo-codes/${promoId}`, { is_active: true }, { headers, withCredentials: true });
      }
      toast.success(`Promo code ${currentActive ? "deactivated" : "activated"}`);
      fetchPromoCodes();
    } catch (error) {
      toast.error("Failed to update promo code");
    }
  };

  // AI Design Generation
  const handleGenerateDesign = async (e) => {
    e.preventDefault();
    if (!aiForm.prompt.trim()) { toast.error("Please enter a design description"); return; }
    setGeneratingDesign(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/ai/generate-design`, aiForm, { headers, withCredentials: true });
      if (response.data.success) {
        toast.success("Design generated successfully!");
        setGeneratedDesigns(prev => [{
          design_id: response.data.design_id, image_base64: response.data.image_base64,
          prompt: aiForm.prompt, style: aiForm.style, product_type: aiForm.product_type
        }, ...prev]);
        setAiForm({ ...aiForm, prompt: "" });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate design");
    } finally {
      setGeneratingDesign(false);
    }
  };

  const handleDeleteDesign = async (designId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/ai/designs/${designId}`, { headers, withCredentials: true });
      setGeneratedDesigns(prev => prev.filter(d => d.design_id !== designId));
      toast.success("Design deleted");
    } catch (error) { toast.error("Failed to delete design"); }
  };

  const downloadDesign = (base64Data, designId) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${base64Data}`;
    link.download = `gas-station-design-${designId}.png`;
    link.click();
  };

  const handleDeleteBeat = async (beatId) => {
    if (!confirm("Are you sure you want to delete this beat?")) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/beats/${beatId}`, { headers, withCredentials: true });
      toast.success("Beat deleted");
      fetchData();
    } catch (error) { toast.error("Failed to delete beat"); }
  };

  const handleDeleteMerch = async (merchId) => {
    if (!confirm("Are you sure you want to delete this merch?")) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/merch/${merchId}`, { headers, withCredentials: true });
      toast.success("Merch deleted");
      fetchData();
    } catch (error) { toast.error("Failed to delete merch"); }
  };

  const handleToggleAdmin = async (userId) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/admin/users/${userId}/admin`, {}, { headers, withCredentials: true });
      toast.success("Admin status updated");
      fetchData();
    } catch (error) { toast.error("Failed to update admin status"); }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-neon-green border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen py-10 px-6" data-testid="admin-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-10">
          <div className="w-12 h-12 bg-neon-green/20 flex items-center justify-center">
            <Shield className="w-6 h-6 text-neon-green" />
          </div>
          <div>
            <Badge className="bg-neon-green/20 text-neon-green border-neon-green/50 mb-1">
              Admin Panel
            </Badge>
            <h1 className="font-heading text-3xl uppercase tracking-tight">Dashboard</h1>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-zinc-900/50 border border-white/10 p-1 flex-wrap h-auto gap-1">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="text-neon-yellow">
              <BarChart3 className="w-4 h-4 mr-1" />Analytics
            </TabsTrigger>
            <TabsTrigger value="promos" data-testid="tab-promos" className="text-neon-green">
              <Tag className="w-4 h-4 mr-1" />Promos
            </TabsTrigger>
            <TabsTrigger value="ai-design" data-testid="tab-ai" className="text-neon-magenta">
              <Sparkles className="w-4 h-4 mr-1" />AI Design
            </TabsTrigger>
            <TabsTrigger value="beats" data-testid="tab-beats">Beats</TabsTrigger>
            <TabsTrigger value="merch" data-testid="tab-merch">Merch</TabsTrigger>
            <TabsTrigger value="cars" data-testid="tab-cars">Cars</TabsTrigger>
            <TabsTrigger value="submissions" data-testid="tab-submissions">Submissions</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="low-stock" data-testid="tab-low-stock" className="text-red-400">
              <AlertTriangle className="w-4 h-4 mr-1" />Low Stock
              {lowStockItems.length > 0 && (
                <span className="ml-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {lowStockItems.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email" className="text-blue-400">
              <Mail className="w-4 h-4 mr-1" />Email
            </TabsTrigger>
            <TabsTrigger value="voice-tag" data-testid="tab-voice-tag" className="text-orange-400">
              <Mic2 className="w-4 h-4 mr-1" />Voice Tag
            </TabsTrigger>
            <TabsTrigger value="blog-admin" data-testid="tab-blog" className="text-white">
              <FileText className="w-4 h-4 mr-1" />Blog
            </TabsTrigger>
            <TabsTrigger value="live-chat" data-testid="tab-chat" className="text-sky-400">
              <MessageCircle className="w-4 h-4 mr-1" />Chat
              {chatSessions.filter(s => s.status === "open").length > 0 && (
                <span className="ml-1 w-5 h-5 bg-sky-500 text-white text-xs rounded-full flex items-center justify-center">
                  {chatSessions.filter(s => s.status === "open").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="affiliates" data-testid="tab-affiliates" className="text-emerald-400">
              <Link2 className="w-4 h-4 mr-1" />Affiliates
            </TabsTrigger>
            <TabsTrigger value="abandoned-carts" data-testid="tab-abandoned-carts" className="text-amber-400">
              <ShoppingCart className="w-4 h-4 mr-1" />Abandoned
              {abandonedCarts.total > 0 && (
                <span className="ml-1 w-5 h-5 bg-amber-500 text-black text-xs rounded-full flex items-center justify-center">
                  {abandonedCarts.total}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="ab-testing" data-testid="tab-ab-testing" className="text-purple-400">
              <FlaskConical className="w-4 h-4 mr-1" />A/B Tests
            </TabsTrigger>
            <TabsTrigger value="rewards" data-testid="tab-rewards" className="text-yellow-400">
              <Trophy className="w-4 h-4 mr-1" />Rewards
            </TabsTrigger>
            <TabsTrigger value="bundles" data-testid="tab-bundles" className="text-purple-300">
              <Package className="w-4 h-4 mr-1" />Bundles
            </TabsTrigger>
            <TabsTrigger value="flash-sales" data-testid="tab-flash-sales" className="text-red-400">
              <Zap className="w-4 h-4 mr-1" />Flash Sales
            </TabsTrigger>
            <TabsTrigger value="gift-cards" data-testid="tab-gift-cards" className="text-yellow-300">
              <Gift className="w-4 h-4 mr-1" />Gift Cards
            </TabsTrigger>
            <TabsTrigger value="producers" data-testid="tab-producers" className="text-neon-cyan">
              <Mic2 className="w-4 h-4 mr-1" />Producers
              {producers.filter(p => p.status === "pending").length > 0 && (
                <span className="ml-1 w-5 h-5 bg-neon-cyan text-black text-xs rounded-full flex items-center justify-center">
                  {producers.filter(p => p.status === "pending").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="tickets" data-testid="tab-tickets" className="text-sky-400">
              <MessageCircle className="w-4 h-4 mr-1" />Tickets
              {adminTickets.filter(t => t.status === "open").length > 0 && (
                <span className="ml-1 w-5 h-5 bg-sky-500 text-white text-xs rounded-full flex items-center justify-center">
                  {adminTickets.filter(t => t.status === "open").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="soundkits-admin" data-testid="tab-soundkits" className="text-orange-400">
              <Layers className="w-4 h-4 mr-1" />Sound Kits
            </TabsTrigger>
            <TabsTrigger value="advanced-analytics" data-testid="tab-advanced-analytics" className="text-emerald-400">
              <BarChart3 className="w-4 h-4 mr-1" />Deep Analytics
            </TabsTrigger>
            <TabsTrigger value="subs-admin" data-testid="tab-subs" className="text-neon-yellow">
              <Crown className="w-4 h-4 mr-1" />Subscriptions
            </TabsTrigger>
            <TabsTrigger value="contests-admin" data-testid="tab-contests-admin" className="text-amber-400">
              <Trophy className="w-4 h-4 mr-1" />Contests
            </TabsTrigger>
            <TabsTrigger value="push-notifs" data-testid="tab-push-notifs" className="text-sky-400">
              <Bell className="w-4 h-4 mr-1" />Push
            </TabsTrigger>
            <TabsTrigger value="printify-setup" data-testid="tab-printify" className="text-neon-green">
              <Package className="w-4 h-4 mr-1" />Printify
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <OverviewTab stats={stats} />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AnalyticsTab analytics={analytics} />
          </TabsContent>

          {/* Promo Codes Tab */}
          <TabsContent value="promos">
            <PromoCodesTab
              promoCodes={promoCodes}
              showPromoDialog={showPromoDialog}
              setShowPromoDialog={setShowPromoDialog}
              promoForm={promoForm}
              setPromoForm={setPromoForm}
              handleCreatePromo={handleCreatePromo}
              handleTogglePromo={handleTogglePromo}
            />
          </TabsContent>

          {/* AI Design Tab */}
          <TabsContent value="ai-design">
            <AIDesignTab
              aiForm={aiForm}
              setAiForm={setAiForm}
              generatingDesign={generatingDesign}
              handleGenerateDesign={handleGenerateDesign}
              generatedDesigns={generatedDesigns}
              handleDeleteDesign={handleDeleteDesign}
              downloadDesign={downloadDesign}
            />
          </TabsContent>

          {/* Beats Tab */}
          <TabsContent value="beats">
            <BeatsTab
              beats={beats}
              showBeatDialog={showBeatDialog}
              setShowBeatDialog={setShowBeatDialog}
              beatForm={beatForm}
              setBeatForm={setBeatForm}
              handleCreateBeat={handleCreateBeat}
              handleDeleteBeat={handleDeleteBeat}
              handleEditStems={handleEditStems}
              handleSoundCloudImport={handleSoundCloudImport}
              scImportLoading={scImportLoading}
            />
          </TabsContent>

          {/* Merch Tab */}
          <TabsContent value="merch">
            <MerchTab
              merch={merch}
              showMerchDialog={showMerchDialog}
              setShowMerchDialog={setShowMerchDialog}
              merchForm={merchForm}
              setMerchForm={setMerchForm}
              handleCreateMerch={handleCreateMerch}
              handleDeleteMerch={handleDeleteMerch}
            />
          </TabsContent>

          {/* Car Culture Tab */}
          <TabsContent value="cars">
            <CarsTab
              showCarDialog={showCarDialog}
              setShowCarDialog={setShowCarDialog}
              carForm={carForm}
              setCarForm={setCarForm}
              handleCreateCar={handleCreateCar}
            />
          </TabsContent>

          {/* Submissions Tab */}
          <TabsContent value="submissions">
            <CarSubmissionsTab
              submissions={carSubmissions}
              handleApprove={handleApproveSubmission}
              handleReject={handleRejectSubmission}
            />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <UsersTab users={users} currentUserId={user.user_id} handleToggleAdmin={handleToggleAdmin} />
          </TabsContent>

          {/* Low Stock Tab */}
          <TabsContent value="low-stock">
            <LowStockTab items={lowStockItems} />
          </TabsContent>

          {/* Email Campaigns Tab */}
          <TabsContent value="email">
            <EmailCampaignsTab
              campaigns={campaigns}
              subscribers={subscribers}
              showCampaignDialog={showCampaignDialog}
              setShowCampaignDialog={setShowCampaignDialog}
              campaignForm={campaignForm}
              setCampaignForm={setCampaignForm}
              handleCreateCampaign={handleCreateCampaign}
            />
          </TabsContent>

          {/* Voice Tag Tab */}
          <TabsContent value="voice-tag">
            <VoiceTagTab
              voiceTagExists={voiceTagExists}
              generatingTag={generatingTag}
              handleGenerateVoiceTag={handleGenerateVoiceTag}
            />
          </TabsContent>

          {/* Blog Admin Tab */}
          <TabsContent value="blog-admin">
            <BlogAdminTab
              blogPosts={blogPosts} showBlogDialog={showBlogDialog} setShowBlogDialog={setShowBlogDialog}
              blogForm={blogForm} setBlogForm={setBlogForm} handleCreateBlogPost={handleCreateBlogPost}
              handleDeleteBlogPost={handleDeleteBlogPost}
              showBulkDialog={showBulkDialog} setShowBulkDialog={setShowBulkDialog}
              bulkBeats={bulkBeats} setBulkBeats={setBulkBeats} handleBulkUpload={handleBulkUpload}
            />
          </TabsContent>

          {/* Live Chat Tab */}
          <TabsContent value="live-chat">
            <LiveChatAdminTab
              chatSessions={chatSessions} activeChatId={activeChatId} openChat={openChat}
              chatMessages={chatMessages} chatReply={chatReply} setChatReply={setChatReply}
              handleSendReply={handleSendReply}
            />
          </TabsContent>

          {/* Affiliates Tab */}
          <TabsContent value="affiliates">
            <AffiliatesTab affiliates={affiliates} payouts={affiliatePayouts} handleApprovePayout={handleApprovePayout} />
          </TabsContent>

          {/* Abandoned Carts Tab */}
          <TabsContent value="abandoned-carts">
            <AbandonedCartsTab carts={abandonedCarts} handleMarkContacted={handleMarkContacted} handleAutoRecover={handleAutoRecover} autoRecoverLoading={autoRecoverLoading} recoveryLogs={recoveryLogs} />
          </TabsContent>

          {/* A/B Testing Tab */}
          <TabsContent value="ab-testing">
            <ABTestingTab
              abTests={abTests} merch={merch}
              showAbTestDialog={showAbTestDialog} setShowAbTestDialog={setShowAbTestDialog}
              abTestForm={abTestForm} setAbTestForm={setAbTestForm}
              handleCreateAbTest={handleCreateAbTest}
              handleToggleAbTest={handleToggleAbTest}
              handleDeleteAbTest={handleDeleteAbTest}
            />
          </TabsContent>

          {/* Rewards Tab */}
          <TabsContent value="rewards">
            <RewardsTab
              overview={loyaltyOverview}
              users={users}
              showAwardDialog={showAwardDialog}
              setShowAwardDialog={setShowAwardDialog}
              awardForm={awardForm}
              setAwardForm={setAwardForm}
              handleAwardPoints={handleAwardPoints}
              showBonusEventDialog={showBonusEventDialog}
              setShowBonusEventDialog={setShowBonusEventDialog}
              bonusEventForm={bonusEventForm}
              setBonusEventForm={setBonusEventForm}
              handleCreateBonusEvent={handleCreateBonusEvent}
              handleDeleteBonusEvent={handleDeleteBonusEvent}
            />
          </TabsContent>

          {/* Bundles Tab */}
          <TabsContent value="bundles">
            <BundlesTab
              bundles={adminBundles}
              beats={beats}
              showBundleDialog={showBundleDialog}
              setShowBundleDialog={setShowBundleDialog}
              bundleForm={bundleForm}
              setBundleForm={setBundleForm}
              handleCreateBundle={handleCreateBundle}
              handleDeleteBundle={handleDeleteBundle}
            />
          </TabsContent>

          {/* Flash Sales Tab */}
          <TabsContent value="flash-sales">
            <FlashSalesTab
              sales={adminFlashSales}
              showFlashSaleDialog={showFlashSaleDialog}
              setShowFlashSaleDialog={setShowFlashSaleDialog}
              flashSaleForm={flashSaleForm}
              setFlashSaleForm={setFlashSaleForm}
              handleCreateFlashSale={handleCreateFlashSale}
              handleDeleteFlashSale={handleDeleteFlashSale}
            />
          </TabsContent>

          {/* Gift Cards Tab */}
          <TabsContent value="gift-cards">
            <GiftCardsTab data={adminGiftCards} />
          </TabsContent>

          {/* Producers Tab */}
          <TabsContent value="producers">
            <ProducersAdminTab
              producers={producers}
              producerBeats={producerBeats}
              handleApproveProducer={handleApproveProducer}
              handleRejectProducer={handleRejectProducer}
              handleApproveProducerBeat={handleApproveProducerBeat}
              handleRejectProducerBeat={handleRejectProducerBeat}
            />
          </TabsContent>

          {/* Tickets Tab */}
          <TabsContent value="tickets">
            <TicketsAdminTab
              tickets={adminTickets}
              handleUpdateStatus={handleUpdateTicketStatus}
              handleReply={handleReplyTicket}
            />
          </TabsContent>

          {/* Sound Kits Admin Tab */}
          <TabsContent value="soundkits-admin">
            <SoundKitsAdminTab
              kits={adminSoundkits}
              showDialog={showSoundkitDialog}
              setShowDialog={setShowSoundkitDialog}
              form={soundkitForm}
              setForm={setSoundkitForm}
              handleCreate={handleCreateSoundkit}
              handleDelete={handleDeleteSoundkit}
            />
          </TabsContent>

          {/* Advanced Analytics Tab */}
          <TabsContent value="advanced-analytics">
            <AdvancedAnalyticsTab data={advancedAnalytics} />
          </TabsContent>

          {/* Subscriptions Admin Tab */}
          <TabsContent value="subs-admin">
            <SubscriptionsAdminTab data={adminSubscriptions} />
          </TabsContent>

          {/* Contests Admin Tab */}
          <TabsContent value="contests-admin">
            <ContestsAdminTab
              contests={adminContests}
              showDialog={showContestDialog}
              setShowDialog={setShowContestDialog}
              form={contestForm}
              setForm={setContestForm}
              handleCreate={handleCreateContest}
              handleUpdateStatus={handleUpdateContestStatus}
            />
          </TabsContent>

          {/* Push Notifications Tab */}
          <TabsContent value="push-notifs">
            <div data-testid="push-notifs-admin">
              <h2 className="font-heading text-xl uppercase mb-6">Push Notifications</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="glass p-6">
                  <h3 className="font-heading text-lg uppercase mb-4 flex items-center gap-2">
                    <Send className="w-4 h-4 text-neon-cyan" /> Send Notification
                  </h3>
                  <form onSubmit={handleSendPush} className="space-y-4">
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={pushForm.title}
                        onChange={(e) => setPushForm({ ...pushForm, title: e.target.value })}
                        className="bg-black/50 border-white/10"
                        placeholder="e.g., New Beat Drop!"
                        required
                        data-testid="push-title-input"
                      />
                    </div>
                    <div>
                      <Label>Message</Label>
                      <Textarea
                        value={pushForm.message}
                        onChange={(e) => setPushForm({ ...pushForm, message: e.target.value })}
                        className="bg-black/50 border-white/10"
                        placeholder="Check out our latest fire beats..."
                        required
                        data-testid="push-message-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Target</Label>
                        <Select value={pushForm.target} onValueChange={(v) => setPushForm({ ...pushForm, target: v })}>
                          <SelectTrigger className="bg-black/50 border-white/10"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-white/10">
                            <SelectItem value="all">All Users</SelectItem>
                            <SelectItem value="subscribers">Subscribers Only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Category</Label>
                        <Select value={pushForm.category} onValueChange={(v) => setPushForm({ ...pushForm, category: v })}>
                          <SelectTrigger className="bg-black/50 border-white/10"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-white/10">
                            <SelectItem value="general">General</SelectItem>
                            <SelectItem value="new_drop">New Drop</SelectItem>
                            <SelectItem value="flash_sale">Flash Sale</SelectItem>
                            <SelectItem value="contest">Contest</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button type="submit" disabled={sendingPush} className="w-full bg-neon-cyan text-black font-bold uppercase" data-testid="send-push-btn">
                      {sendingPush ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Push Notification
                    </Button>
                  </form>
                </div>
                <div className="glass p-6">
                  <h3 className="font-heading text-lg uppercase mb-4">Recent Notifications</h3>
                  {pushHistory.length === 0 ? (
                    <p className="text-zinc-500 text-sm text-center py-8">No notifications sent yet</p>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {pushHistory.map((n, idx) => (
                        <div key={idx} className="p-3 bg-white/5 border border-white/5">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-sm font-medium">{n.title}</p>
                            <Badge variant="outline" className="text-xs">{n.category}</Badge>
                          </div>
                          <p className="text-xs text-zinc-400">{n.message}</p>
                          <div className="flex justify-between mt-2 text-xs text-zinc-600">
                            <span>{n.recipients} recipients</span>
                            <span>{new Date(n.sent_at).toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Printify Setup Tab */}
          <TabsContent value="printify-setup">
            <div data-testid="printify-admin" className="space-y-6">
              <h2 className="font-heading text-xl uppercase flex items-center gap-2">
                <Package className="w-5 h-5 text-neon-green" /> Printify Fulfillment Setup
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Status Card */}
                <div className="glass p-6 space-y-4">
                  <h3 className="font-heading text-sm uppercase text-zinc-400">Current Status</h3>
                  {!printifyStatus ? (
                    <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
                  ) : printifyStatus.error ? (
                    <p className="text-red-400 text-sm">{printifyStatus.error}</p>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-zinc-400">Printify API</span>
                        <Badge className={printifyStatus.is_configured ? "bg-neon-green/20 text-neon-green border-neon-green/30" : "bg-red-500/20 text-red-400"}>
                          {printifyStatus.is_configured ? "Connected" : "Not Configured"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-zinc-400">Products Linked</span>
                        <span className="text-white font-mono">{printifyStatus.linked_to_printify} / {printifyStatus.total_products}</span>
                      </div>
                      <div className="w-full bg-zinc-800 h-2">
                        <div
                          className="h-2 bg-neon-green transition-all"
                          style={{ width: `${printifyStatus.total_products > 0 ? (printifyStatus.linked_to_printify / printifyStatus.total_products) * 100 : 0}%` }}
                        />
                      </div>
                      {printifyStatus.setup_complete ? (
                        <p className="text-neon-green text-sm flex items-center gap-2"><CheckCircle className="w-4 h-4" /> All products linked! Orders will auto-fulfill.</p>
                      ) : (
                        <p className="text-yellow-400 text-sm">{printifyStatus.unlinked} products not yet linked to Printify</p>
                      )}
                      {printifyStatus.unlinked_products?.length > 0 && (
                        <div className="mt-3 max-h-32 overflow-y-auto space-y-1">
                          {printifyStatus.unlinked_products.map((p, i) => (
                            <div key={i} className="text-xs text-zinc-500 flex justify-between">
                              <span>{p.name}</span><span className="text-zinc-600">{p.category}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Setup Action Card */}
                <div className="glass p-6 space-y-4">
                  <h3 className="font-heading text-sm uppercase text-zinc-400">One-Click Setup</h3>
                  <div className="space-y-3 text-sm text-zinc-300">
                    <p>This will:</p>
                    <ol className="list-decimal list-inside space-y-1 text-zinc-400">
                      <li>Upload your 5 collection artworks to Printify</li>
                      <li>Create all 51 products on your Printify store</li>
                      <li>Link each product so orders auto-fulfill</li>
                    </ol>
                    <p className="text-yellow-400 text-xs mt-2">Note: This may take 2-3 minutes. Do not close this tab.</p>
                  </div>

                  {/* Collection Artworks Preview */}
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { name: "Idle to Redline", url: "https://static.prod-images.emergentagent.com/jobs/d2d9cfa6-179f-4d82-b149-d65e957345e8/images/ba1a49eefd7c32486b5310f5e35a582ea5ff8cacf1dc3907fbdd00eae34ad5c0.png" },
                      { name: "GasMadeTheBeat", url: "https://static.prod-images.emergentagent.com/jobs/d2d9cfa6-179f-4d82-b149-d65e957345e8/images/65b32201689130ecce33d4389ab3f474c06979d197df53a44823c005f976b78c.png" },
                      { name: "Gapped & Glorious", url: "https://static.prod-images.emergentagent.com/jobs/d2d9cfa6-179f-4d82-b149-d65e957345e8/images/f20eb7dceb4ba6fc5edf6c2a6b8bc236c5e7f6f612de9a1916b172194751bf4b.png" },
                      { name: "Neon Tokyo", url: "https://static.prod-images.emergentagent.com/jobs/d2d9cfa6-179f-4d82-b149-d65e957345e8/images/496983e7bafbd9742ac642eeae6a738861c55a92d842b5108facb44438c8af9c.png" },
                      { name: "GS Essentials", url: "https://static.prod-images.emergentagent.com/jobs/d2d9cfa6-179f-4d82-b149-d65e957345e8/images/21c4b1eb6d0372a7a570eecbcb5d4f942f3a95e8ee11aff394f6b53ab1562b0c.png" },
                    ].map((art, i) => (
                      <div key={i} className="text-center">
                        <img src={art.url} alt={art.name} className="w-full aspect-square object-cover border border-white/10 mb-1" />
                        <p className="text-xs text-zinc-500 truncate">{art.name}</p>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={handlePrintifySetup}
                    disabled={printifySetupLoading}
                    className="w-full bg-neon-green text-black font-bold uppercase tracking-widest"
                    data-testid="printify-setup-btn"
                  >
                    {printifySetupLoading
                      ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Setting Up Printify...</>
                      : <><Package className="w-4 h-4 mr-2" />Auto-Setup All Products on Printify</>
                    }
                  </Button>
                </div>
              </div>

              {/* Results */}
              {printifySetupResult && (
                <div className="glass p-6 space-y-4" data-testid="printify-results">
                  <h3 className="font-heading text-sm uppercase text-zinc-400">Setup Results</h3>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-3 bg-neon-green/10 border border-neon-green/20">
                      <p className="text-2xl font-bold text-neon-green">{printifySetupResult.summary?.created || 0}</p>
                      <p className="text-xs text-zinc-400">Created</p>
                    </div>
                    <div className="p-3 bg-red-500/10 border border-red-500/20">
                      <p className="text-2xl font-bold text-red-400">{printifySetupResult.summary?.failed || 0}</p>
                      <p className="text-xs text-zinc-400">Failed</p>
                    </div>
                    <div className="p-3 bg-yellow-500/10 border border-yellow-500/20">
                      <p className="text-2xl font-bold text-yellow-400">{printifySetupResult.summary?.skipped || 0}</p>
                      <p className="text-xs text-zinc-400">Skipped</p>
                    </div>
                  </div>
                  {printifySetupResult.failed?.length > 0 && (
                    <div>
                      <p className="text-sm text-red-400 mb-2">Failed products:</p>
                      {printifySetupResult.failed.map((f, i) => (
                        <div key={i} className="text-xs text-zinc-500 flex justify-between py-1 border-b border-white/5">
                          <span>{f.name}</span><span className="text-red-400/60">{f.reason}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Webhook Management */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="glass p-6 space-y-4">
                  <h3 className="font-heading text-sm uppercase text-zinc-400 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-neon-yellow" /> Printify Webhooks
                  </h3>
                  <p className="text-xs text-zinc-500">Webhooks let Printify push order status updates instantly — no polling needed.</p>

                  {webhookStatus?.error ? (
                    <p className="text-red-400 text-xs">{webhookStatus.error}</p>
                  ) : webhookStatus ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-zinc-400">Status</span>
                        <Badge className={webhookStatus.is_complete
                          ? "bg-neon-green/20 text-neon-green border-neon-green/30"
                          : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"}>
                          {webhookStatus.is_complete ? "Fully Registered" : `${webhookStatus.registered_topics?.length || 0}/${webhookStatus.expected_topics?.length || 3} topics`}
                        </Badge>
                      </div>
                      {webhookStatus.gas_station_webhooks?.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {webhookStatus.gas_station_webhooks.map((wh, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-zinc-500">
                              <CheckCircle className="w-3 h-3 text-neon-green flex-shrink-0" />
                              <span className="font-mono">{wh.topic}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-zinc-500 text-xs"><Loader2 className="w-3 h-3 animate-spin" /> Loading...</div>
                  )}

                  <Button
                    onClick={handleRegisterWebhooks}
                    disabled={webhookLoading}
                    className="w-full bg-neon-yellow text-black font-bold uppercase tracking-widest text-sm"
                    data-testid="register-webhooks-btn"
                  >
                    {webhookLoading
                      ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Registering...</>
                      : <><Zap className="w-4 h-4 mr-2" />{webhookStatus?.is_complete ? "Re-Register Webhooks" : "Register Webhooks"}</>
                    }
                  </Button>
                </div>

                {/* Manual Sync */}
                <div className="glass p-6 space-y-4">
                  <h3 className="font-heading text-sm uppercase text-zinc-400 flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-neon-cyan" /> Tracking Sync
                  </h3>
                  <div className="space-y-2 text-xs text-zinc-400">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-neon-green animate-pulse" />
                      <span>Auto-sync runs every <strong className="text-white">6 hours</strong></span>
                    </div>
                    <p className="text-zinc-500">Checks Printify for tracking updates on all in-progress merch orders. You can also trigger it manually below.</p>
                  </div>
                  <Button
                    onClick={handleManualSync}
                    disabled={syncLoading}
                    variant="outline"
                    className="w-full border-neon-cyan/40 text-neon-cyan hover:bg-neon-cyan/10 font-bold uppercase tracking-widest text-sm"
                    data-testid="manual-sync-btn"
                  >
                    {syncLoading
                      ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Syncing...</>
                      : <><RefreshCw className="w-4 h-4 mr-2" />Sync All Orders Now</>
                    }
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

        </Tabs>
        <Dialog open={showStemsDialog} onOpenChange={setShowStemsDialog}>
          <DialogContent className="bg-[#09090b] border-white/10 sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-white">Manage Stems / Trackouts</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {stemsData.map((stem, idx) => (
                <div key={idx} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs text-zinc-500">Track Name</Label>
                    <Input
                      value={stem.name}
                      onChange={(e) => {
                        const updated = [...stemsData];
                        updated[idx].name = e.target.value;
                        setStemsData(updated);
                      }}
                      placeholder="e.g., Drums, Melody"
                      className="bg-black/50 border-white/10"
                      data-testid={`stem-name-${idx}`}
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-zinc-500">Audio URL</Label>
                    <Input
                      value={stem.audio_url}
                      onChange={(e) => {
                        const updated = [...stemsData];
                        updated[idx].audio_url = e.target.value;
                        setStemsData(updated);
                      }}
                      placeholder="https://..."
                      className="bg-black/50 border-white/10"
                      data-testid={`stem-url-${idx}`}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setStemsData(stemsData.filter((_, i) => i !== idx))}
                    className="text-red-400 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStemsData([...stemsData, { name: "", audio_url: "" }])}
                className="border-white/20"
                data-testid="add-stem-button"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Stem
              </Button>
              <Button
                onClick={handleSaveStems}
                className="w-full bg-neon-green text-black font-bold uppercase"
                data-testid="save-stems-button"
              >
                <Save className="w-4 h-4 mr-2" /> Save Stems
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPage;

